import { useMemo, useState } from "react"
import { SketchDialog } from "@/components/sketch/sketch-dialog"
import { buildLocationAngleFacingItems } from "@/components/locations/location-angles-picker-dialog"
import { AddDashedTile } from "@/components/ui/add-dashed-tile"
import { EntityMultiViewThumb } from "@/components/workspace/entity-multi-view-media-panel"
import { useTranslation } from "@/hooks/use-translation"
import {
  LOCATION_FACINGS,
  PRIMARY_SHOT_LOCATION_FACING,
  type LocationFacing,
  type LocationStorableFacing,
  type LocationViewpointRow,
  locationViewpointDownloadUrl,
} from "@/lib/locations-api"

function fullUrlForFacing(
  facing: LocationFacing,
  previewUrl: string | null,
  referenceDownloadUrlsByFacing?: Partial<Record<LocationStorableFacing, string | null>>,
  primaryFallbackDownloadUrl?: string | null,
): string | null {
  const download = referenceDownloadUrlsByFacing?.[facing]
  if (download) return download
  if (facing === PRIMARY_SHOT_LOCATION_FACING && primaryFallbackDownloadUrl) {
    return primaryFallbackDownloadUrl
  }
  return previewUrl
}

type ShotLocationAnglesProps = {
  projectId: number
  selectedFacing: LocationFacing
  selectedViewpointId: number | null
  referenceUrlsByFacing?: Partial<Record<LocationFacing, string | null>>
  referenceThumbUrlsByFacing?: Partial<Record<LocationFacing, string | null>>
  referenceDownloadUrlsByFacing?: Partial<Record<LocationStorableFacing, string | null>>
  primaryFallbackUrl?: string | null
  primaryFallbackDownloadUrl?: string | null
  viewpoints: LocationViewpointRow[]
  onSelectFacing: (facing: LocationFacing) => void
  onSelectViewpoint: (viewpoint: LocationViewpointRow) => void
  onOpenRoomViewer?: () => void
  className?: string
}

export function ShotLocationAngles({
  projectId,
  selectedFacing,
  selectedViewpointId,
  referenceUrlsByFacing,
  referenceThumbUrlsByFacing,
  referenceDownloadUrlsByFacing,
  primaryFallbackUrl,
  primaryFallbackDownloadUrl,
  viewpoints,
  onSelectFacing,
  onSelectViewpoint,
  onOpenRoomViewer,
  className,
}: ShotLocationAnglesProps) {
  const { t } = useTranslation(["shot", "workspace"])
  const [preview, setPreview] = useState<{ url: string; title: string } | null>(null)

  const facingItems = useMemo(
    () =>
      buildLocationAngleFacingItems(
        referenceUrlsByFacing,
        referenceThumbUrlsByFacing,
        (facing) => t(`location.facing.${facing}`),
        primaryFallbackUrl,
      ),
    [referenceUrlsByFacing, referenceThumbUrlsByFacing, primaryFallbackUrl, t],
  )

  const facingSelected = selectedViewpointId == null
  const showAdditional = Boolean(onOpenRoomViewer) || viewpoints.length > 0
  const roomViewerLabel = t("workspace:locationsPage.roomViewer.open")

  const shortLabel = (facing: LocationFacing) => {
    if (facing === "three_quarter") return t("location.facing.short.three_quarter")
    if (facing === "topview") return t("location.facing.short.topview")
    if (facing === "north") return t("location.facing.letter.north")
    if (facing === "east") return t("location.facing.letter.east")
    if (facing === "south") return t("location.facing.letter.south")
    return t("location.facing.letter.west")
  }

  return (
    <div className={className}>
      <p className="mb-2 text-[10px] font-medium tracking-wide text-ui-text-muted uppercase">
        {t("location.angles.basic")}
      </p>

      <div
        className="it2tv-scrollbar -mx-0.5 overflow-x-auto px-0.5 pb-1"
        role="group"
        aria-label={t("location.angles.basic")}
      >
        <div className="flex w-max gap-1">
          {LOCATION_FACINGS.map((facing) => {
            const item = facingItems.find((f) => f.facing === facing)
            const ownUrl = referenceUrlsByFacing?.[facing]
            // Primary (3/4) may only exist as reference_url / sheet on legacy variants.
            const url =
              ownUrl ??
              (facing === PRIMARY_SHOT_LOCATION_FACING ? (primaryFallbackUrl ?? item?.url) : item?.url) ??
              null
            const label = shortLabel(facing)
            const fullLabel = t(`location.facing.${facing}`)
            return (
              <EntityMultiViewThumb
                key={facing}
                url={url}
                thumbUrl={referenceThumbUrlsByFacing?.[facing] ?? url}
                selected={facingSelected && selectedFacing === facing}
                label={label}
                onSelect={() => onSelectFacing(facing)}
                onPreview={
                  url
                    ? () => {
                        const fullUrl = fullUrlForFacing(
                          facing,
                          url,
                          referenceDownloadUrlsByFacing,
                          primaryFallbackDownloadUrl,
                        )
                        if (!fullUrl) return
                        setPreview({ url: fullUrl, title: fullLabel })
                      }
                    : undefined
                }
                previewTitle={t("location.openImage", { name: fullLabel })}
                compact
                showCaptionBelow
              />
            )
          })}
        </div>
      </div>

      {showAdditional ? (
        <div className="mt-3 border-t border-ui-border pt-3">
          <p className="mb-2 text-[10px] font-medium tracking-wide text-ui-text-muted uppercase">
            {t("location.angles.photographed")}
          </p>
          <div
            className="it2tv-scrollbar -mx-0.5 overflow-x-auto px-0.5 pb-1"
            role="group"
            aria-label={t("location.angles.photographed")}
          >
            <div className="flex w-max gap-1">
              {onOpenRoomViewer ? (
                <div className="flex shrink-0 flex-col items-center gap-1.5">
                  <AddDashedTile
                    label="3D"
                    onClick={onOpenRoomViewer}
                    ariaLabel={roomViewerLabel}
                    title={roomViewerLabel}
                    className="size-11 h-11 w-11 text-[10px]"
                  />
                </div>
              ) : null}
              {viewpoints.map((vp, index) => {
                const label =
                  vp.name?.trim() || t("workspace:locationsPage.angles.additionalItemIndexed", { index: index + 1 })
                const url = vp.url
                const sourceUrl = locationViewpointDownloadUrl(vp)
                return (
                  <EntityMultiViewThumb
                    key={vp.id}
                    url={url}
                    thumbUrl={vp.thumb_url ?? vp.url}
                    selected={selectedViewpointId === vp.id}
                    label={label}
                    onSelect={() => onSelectViewpoint(vp)}
                    onPreview={sourceUrl ? () => setPreview({ url: sourceUrl, title: label }) : undefined}
                    previewTitle={t("location.openImage", { name: label })}
                    compact
                    showCaptionBelow
                  />
                )
              })}
            </div>
          </div>
        </div>
      ) : null}

      <SketchDialog
        open={!!preview}
        onOpenChange={(o) => !o && setPreview(null)}
        projectId={projectId}
        sourceUrl={preview?.url ?? null}
        presentation="view"
        allowEdit={false}
        persist="none"
        nested
        headerTitle={preview?.title}
      />
    </div>
  )
}
